/**
 * Inline Type Selector Component
 *
 * Shared component for selecting field types with a Select dropdown.
 * Used by both Create Table wizard and Table Preview.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import {
  FIELD_DATA_TYPES,
  FIELD_DATA_TYPE_LABELS,
  type FieldDataType,
} from '@loader/shared-types';

import { getTypeIcon } from '../constants/fieldTypes';

interface InlineTypeSelectorProps {
  /** Current data type */
  dataType: string;
  /** Called when type changes */
  onTypeChange: (type: FieldDataType) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function InlineTypeSelector({
  dataType,
  onTypeChange,
  disabled,
}: InlineTypeSelectorProps) {
  const CurrentIcon = getTypeIcon(dataType);
  const currentLabel =
    FIELD_DATA_TYPE_LABELS[dataType as FieldDataType] || dataType;

  return (
    <Select
      value={dataType}
      onValueChange={(value) => onTypeChange(value as FieldDataType)}
      disabled={disabled}
    >
      <SelectTrigger size="sm" className="w-full">
        <SelectValue>
          <CurrentIcon className="h-3.5 w-3.5" />
          <span>{currentLabel}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        {FIELD_DATA_TYPES.map((type) => {
          const Icon = getTypeIcon(type);
          const label = FIELD_DATA_TYPE_LABELS[type];

          return (
            <SelectItem key={type} value={type}>
              <Icon className="h-3.5 w-3.5" />
              <span>{label}</span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
